import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { UnifiedWorkerProfile, WorkerPopupConfig } from './types';

// Thresholds for auto-sync
const AUTO_SYNC_STALE_SECONDS = 3600; // 1 hour
const AUTO_SYNC_COMPLETENESS_THRESHOLD = 50; // 50%

interface WorkerPopupState {
  isOpen: boolean;
  identifier: string | null;
  profile: UnifiedWorkerProfile | null;
  loading: boolean;
  error: string | null;
  refreshing: boolean;
}

interface WorkerPopupContextValue {
  state: WorkerPopupState;
  config: WorkerPopupConfig;
  openPopup: (identifier: string | number) => void;
  closePopup: () => void;
  fetchProfile: (identifier: string | number) => Promise<UnifiedWorkerProfile | null>;
  refreshProfile: () => Promise<void>;
}

const WorkerPopupContext = createContext<WorkerPopupContextValue | null>(null);

export interface WorkerPopupProviderProps {
  children: React.ReactNode;
  apiBaseUrl: string;
  currentAppId?: WorkerPopupConfig['currentAppId'];
  getAuthToken?: () => Promise<string | null>;
  appUrls?: WorkerPopupConfig['appUrls'];
}

export const WorkerPopupProvider: React.FC<WorkerPopupProviderProps> = ({
  children,
  apiBaseUrl,
  currentAppId,
  getAuthToken,
  appUrls,
}) => {
  const [state, setState] = useState<WorkerPopupState>({
    isOpen: false,
    identifier: null,
    profile: null,
    loading: false,
    error: null,
    refreshing: false,
  });

  const config = useMemo<WorkerPopupConfig>(
    () => ({
      apiBaseUrl,
      currentAppId,
      getAuthToken,
      appUrls,
    }),
    [apiBaseUrl, currentAppId, getAuthToken, appUrls],
  );

  const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (getAuthToken) {
      const token = await getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return headers;
  }, [getAuthToken]);

  const fetchProfile = useCallback(
    async (identifier: string | number): Promise<UnifiedWorkerProfile | null> => {
      const id = String(identifier);
      try {
        const headers = await getHeaders();

        // Build URL with query params to include all data
        const queryParams = new URLSearchParams({
          includeSyncInfo: 'true',
          includeCompleteness: 'true',
          includeHousing: 'true',
          includeWorkedHours: 'true',
          includeExtended: 'true',
          includeHistory: 'true',
          historyLimit: '50',
          hoursWeeksLimit: '52',
        });

        const response = await fetch(
          `${apiBaseUrl}/workers/unified-profile/${encodeURIComponent(id)}?${queryParams}`,
          { headers },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        const data = await response.json();
        return data as UnifiedWorkerProfile;
      } catch (err: any) {
        console.error('Failed to fetch worker profile:', err);
        throw err;
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const syncWorker = useCallback(
    async (identifier: string | number): Promise<void> => {
      const id = String(identifier);
      try {
        const headers = await getHeaders();
        const response = await fetch(`${apiBaseUrl}/workers/${encodeURIComponent(id)}/sync`, {
          method: 'POST',
          headers,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }
      } catch (err: any) {
        console.error('Failed to sync worker:', err);
        throw err;
      }
    },
    [apiBaseUrl, getHeaders],
  );

  // Poll until sync completes (status changes from SYNCING)
  const waitForSyncCompletion = useCallback(
    async (identifier: string | number, maxAttempts = 15): Promise<UnifiedWorkerProfile | null> => {
      const pollIntervalMs = 2000; // 2 seconds between polls

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        // Wait before polling (except first attempt)
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        }

        const profile = await fetchProfile(identifier);

        // Check if sync is complete (status is no longer SYNCING)
        const syncStatus = profile?.sync?.syncStatus;
        if (syncStatus !== 'SYNCING') {
          return profile;
        }
      }

      // Timeout - return last profile even if still syncing
      console.warn('Sync timeout - returning profile while still syncing');
      return fetchProfile(identifier);
    },
    [fetchProfile],
  );

  const shouldAutoSync = useCallback((profile: UnifiedWorkerProfile | null): boolean => {
    if (!profile) return false;

    // Check if data is stale (older than threshold)
    const dataAgeSeconds = profile.sync?.dataAgeSeconds;
    if (dataAgeSeconds !== undefined && dataAgeSeconds > AUTO_SYNC_STALE_SECONDS) {
      return true;
    }

    // Check if completeness is below threshold
    const completenessScore = profile.completeness?.score;
    if (completenessScore !== undefined && completenessScore < AUTO_SYNC_COMPLETENESS_THRESHOLD) {
      return true;
    }

    return false;
  }, []);

  const refreshProfile = useCallback(async (): Promise<void> => {
    const id = state.identifier;
    if (!id) return;

    setState((prev) => ({ ...prev, refreshing: true }));

    try {
      // First trigger sync with EasyFlex
      await syncWorker(id);
      // Wait for sync to complete (polls until status changes from SYNCING)
      const profile = await waitForSyncCompletion(id);
      setState((prev) => ({
        ...prev,
        profile,
        refreshing: false,
      }));
    } catch (err: any) {
      console.error('Failed to refresh profile:', err);
      setState((prev) => ({
        ...prev,
        refreshing: false,
        error: err?.message || 'Failed to refresh profile',
      }));
    }
  }, [state.identifier, syncWorker, waitForSyncCompletion]);

  const openPopup = useCallback(
    async (identifier: string | number) => {
      const id = String(identifier);
      setState((prev) => ({
        ...prev,
        isOpen: true,
        identifier: id,
        loading: true,
        error: null,
        refreshing: false,
      }));

      try {
        // First fetch the profile
        let profile = await fetchProfile(id);
        setState((prev) => ({
          ...prev,
          profile,
          loading: false,
        }));

        // Check if auto-sync is needed (stale data or low completeness)
        if (shouldAutoSync(profile)) {
          setState((prev) => ({ ...prev, refreshing: true }));
          try {
            await syncWorker(id);
            // Wait for sync to complete (polls until status changes from SYNCING)
            profile = await waitForSyncCompletion(id);
            setState((prev) => ({
              ...prev,
              profile,
              refreshing: false,
            }));
          } catch (syncErr: any) {
            // Auto-sync failed, but we still have the original profile
            console.warn('Auto-sync failed:', syncErr);
            setState((prev) => ({ ...prev, refreshing: false }));
          }
        }
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || 'Failed to load worker profile',
        }));
      }
    },
    [fetchProfile, shouldAutoSync, syncWorker, waitForSyncCompletion],
  );

  const closePopup = useCallback(() => {
    setState({
      isOpen: false,
      identifier: null,
      profile: null,
      loading: false,
      error: null,
      refreshing: false,
    });
  }, []);

  const value = useMemo<WorkerPopupContextValue>(
    () => ({
      state,
      config,
      openPopup,
      closePopup,
      fetchProfile,
      refreshProfile,
    }),
    [state, config, openPopup, closePopup, fetchProfile, refreshProfile],
  );

  return (
    <WorkerPopupContext.Provider value={value}>
      {children}
    </WorkerPopupContext.Provider>
  );
};

export const useWorkerPopupContext = (): WorkerPopupContextValue => {
  const context = useContext(WorkerPopupContext);
  if (!context) {
    throw new Error('useWorkerPopupContext must be used within WorkerPopupProvider');
  }
  return context;
};

/**
 * Safe version that returns null if not within a provider.
 * Use this when the component can work both with and without a provider.
 */
export const useWorkerPopupContextSafe = (): WorkerPopupContextValue | null => {
  return useContext(WorkerPopupContext);
};

export default WorkerPopupProvider;
